import React, { type ComponentType } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { ModalBody, ModalFooter } from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';
import { Box, xcss } from '@atlaskit/primitives';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import type { IntlShape } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import Header from './header/index.tsx';
import messages from './messages.tsx';

export type Props = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	Content: ComponentType<Record<any, any>>;
	onClose: () => void;
	intl: IntlShape;
};

function Modal(props: Props) {
	const {
		Content,
		onClose,
		intl: { formatMessage },
	} = props;

	return (
		<ShortcutScope>
			<ModalDialog
				label={formatMessage(messages.heading)}
				onClose={onClose}
				autoFocus={false}
				shouldCloseOnOverlayClick={false}
				messageId="navigation-shortcuts.manage-pages-link-modal.modal.modal-dialog"
				messageType="transactional"
			>
				<Header />
				<ModalBody>
					<ContentWrapper>
						{fg('blu-6207-a11y-empty-states-headings-images') ? (
							<Box xcss={headingWrapperStyles}>
								<Heading size="large" as="h1">
									{formatMessage(messages.heading)}
								</Heading>
							</Box>
						) : (
							<HeadingOld>{formatMessage(messages.heading)}</HeadingOld>
						)}
						<Content />
						<ViewTracker />
					</ContentWrapper>
				</ModalBody>
				<ModalFooter>
					<Button appearance="link" onClick={onClose}>
						{formatMessage(messages.close)}
					</Button>
				</ModalFooter>
			</ModalDialog>
		</ShortcutScope>
	);
}

export default Modal;

const headingWrapperStyles = xcss({
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingOld = styled.h2({
	paddingTop: token('space.100'),
	paddingRight: 0,
	paddingBottom: token('space.100'),
	paddingLeft: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	paddingTop: 0,
	paddingRight: token('space.200'),
	paddingBottom: 0,
	paddingLeft: token('space.200'),
});
